/**
 * MasterDataLogisticCostFormDialog — Create/Edit `logistics_cost`.
 * 1:1 port of Access `frmMdQryLogisticCost` Add. Compact Sales Contract style.
 *
 * PK is DB-generated auto-int `id` (no PK field in create; readonly in edit).
 * `date` is a data column (editable in BOTH create AND edit). Single-column
 * Grid, field order: [id (edit only, readonly)] → date → value (Cost) → note.
 * value Format Standard (#,##0.00 — type=text + format on blur).
 */
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCreateMdLogisticCostMutation,
  useUpdateMdLogisticCostMutation,
} from '@/store/api/masterDataCrudApi'
import type { MasterDataLogisticCost } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import { compactDialogContentSx, dialogHeaderBtnSx, dialogTitleActionsSx, dialogTitleSx, dialogTitleTextSx, numSx, roSx } from '../purchase/dialogStyle'

export interface Props {
  open: boolean
  mode: 'create' | 'edit'
  initialValue?: MasterDataLogisticCost | null
  onClose: () => void
  onSaved?: () => void
}

type FormState = {
  date: string
  value: string
  note: string
}

const EMPTY: FormState = { date: '', value: '', note: '' }

// Access Format='Standard' → #,##0.00. type=text để giữ dấu phẩy; format on blur.
const parseNumeric = (s: string): number | null => {
  const t = (s ?? '').replace(/,/g, '').trim()
  if (t === '') return null
  const n = Number(t)
  return Number.isNaN(n) ? null : n
}
const formatStandard = (v: string | number | null | undefined): string => {
  if (v == null || v === '') return ''
  const n = typeof v === 'number' ? v : Number(String(v).replace(/,/g, ''))
  if (Number.isNaN(n)) return String(v)
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
// Chỉ cho nhập số: bỏ mọi ký tự không phải chữ số/dấu chấm, giữ tối đa 1 dấu chấm.
const sanitizeNumeric = (raw: string): string => {
  let t = raw.replace(/[^0-9.]/g, '')
  const i = t.indexOf('.')
  if (i !== -1) t = t.slice(0, i + 1) + t.slice(i + 1).replace(/\./g, '')
  return t
}

export default function MasterDataLogisticCostFormDialog({
  open,
  mode,
  initialValue,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [createLc, { isLoading: creating }] = useCreateMdLogisticCostMutation()
  const [updateLc, { isLoading: updating }] = useUpdateMdLogisticCostMutation()
  const loading = creating || updating

  const [form, setForm] = useState<FormState>(EMPTY)
  const [showErrors, setShowErrors] = useState(false)

  useEffect(() => {
    if (!open) return
    setShowErrors(false)
    if (isEdit && initialValue) {
      setForm({
        date: initialValue.date ?? '',
        value: formatStandard(initialValue.value),
        note: initialValue.note ?? '',
      })
    } else {
      // New: auto-load hôm nay vào Date (Access default = Now).
      setForm({ ...EMPTY, date: dayjs().format('YYYY-MM-DD') })
    }
  }, [open, isEdit, initialValue])

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }))

  const dateEmpty = !form.date
  const canSave = !dateEmpty && !loading

  const handleClose = () => {
    if (loading) return
    onClose()
  }

  const handleSave = async () => {
    if (dateEmpty) {
      setShowErrors(true)
      return
    }
    const body = {
      date: form.date,
      value: parseNumeric(form.value),
      note: form.note.trim() || null,
    }
    try {
      if (isEdit && initialValue) {
        await updateLc({ id: initialValue.id, body }).unwrap()
        snack.success('Logistic cost updated')
      } else {
        await createLc(body).unwrap()
        snack.success('Logistic cost created')
      }
      onSaved?.()
      onClose()
    } catch (e) {
      snack.error(getErrorMessage(e, isEdit ? 'Failed to update' : 'Failed to create'))
    }
  }

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (loading) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" noWrap sx={dialogTitleTextSx}>
          {isEdit ? 'Edit Logistic Cost' : 'New Logistic Cost'}
          {isEdit && initialValue ? (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              #{initialValue.id}
            </Typography>
          ) : null}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={!canSave}
            sx={dialogHeaderBtnSx}
          >
            {loading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" size="small" onClick={handleClose} disabled={loading} sx={dialogHeaderBtnSx}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          {isEdit && initialValue ? (
            <Grid item xs={12}>
              <TextField
                label="ID"
                size="small"
                fullWidth
                value={String(initialValue.id)}
                helperText="ID is auto-generated."
                InputLabelProps={{ shrink: true }}
                InputProps={{ readOnly: true }}
                inputProps={{ tabIndex: -1 }}
                sx={roSx}
              />
            </Grid>
          ) : null}
          <Grid item xs={12}>
            <DatePicker
              label="Date"
              format="DD-MMM-YYYY"
              value={form.date ? dayjs(form.date) : null}
              onChange={(d) => set('date', d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  required: true,
                  error: showErrors && dateEmpty,
                  InputLabelProps: { shrink: true },
                  inputProps: { tabIndex: 1 },
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Cost"
              size="small"
              fullWidth
              value={form.value}
              onChange={(e) => set('value', sanitizeNumeric(e.target.value))}
              onBlur={() => set('value', formatStandard(form.value))}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 2, inputMode: 'decimal', style: { textAlign: 'right' } }}
              sx={numSx}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Note"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={form.note}
              onChange={(e) => set('note', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 3 }}
            />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
